export type CountryOption = {
  code: string;
  name: string;
};

export type CameraLocationSearchResult = {
  latitude: number;
  longitude: number;
  displayName: string;
  countryCode: string;
  countryName: string;
};

const NOMINATIM_SEARCH_URL = "https://nominatim.openstreetmap.org/search";
const MIN_REQUEST_INTERVAL_MS = 1100;
const MAX_CACHE_ENTRIES = 50;
const NON_COUNTRY_REGION_CODES = new Set([
  "AC",
  "CP",
  "DG",
  "EA",
  "EU",
  "EZ",
  "IC",
  "QO",
  "TA",
  "UN",
]);

const FALLBACK_COUNTRIES: CountryOption[] = [
  { code: "CA", name: "Canada" },
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "YE", name: "Yemen" },
  { code: "EG", name: "Egypt" },
  { code: "CN", name: "China" },
];

const searchCache = new Map<string, CameraLocationSearchResult[]>();
let lastRequestAt = 0;

function browserLocale() {
  if (typeof navigator === "undefined") return "en";
  return navigator.language || "en";
}

export function buildCountryOptions(locale = browserLocale()): CountryOption[] {
  try {
    if (typeof Intl.DisplayNames !== "function") return FALLBACK_COUNTRIES;
    const names = new Intl.DisplayNames([locale], {
      type: "region",
      fallback: "code",
    });
    const countries: CountryOption[] = [];

    for (let first = 65; first <= 90; first += 1) {
      for (let second = 65; second <= 90; second += 1) {
        const code = String.fromCharCode(first, second);
        if (NON_COUNTRY_REGION_CODES.has(code)) continue;
        const name = names.of(code);
        if (!name || name === code || /unknown region/i.test(name)) continue;
        countries.push({ code, name });
      }
    }

    if (!countries.length) return FALLBACK_COUNTRIES;
    return countries.sort((a, b) => a.name.localeCompare(b.name, locale));
  } catch {
    return FALLBACK_COUNTRIES;
  }
}

export function normalizeLocationSearchResult(
  value: any,
): CameraLocationSearchResult | null {
  const latitude = Number(value?.lat);
  const longitude = Number(value?.lon);
  const displayName = String(value?.display_name || "").trim();
  if (
    !displayName ||
    !Number.isFinite(latitude) ||
    latitude < -90 ||
    latitude > 90 ||
    !Number.isFinite(longitude) ||
    longitude < -180 ||
    longitude > 180
  ) {
    return null;
  }

  return {
    latitude,
    longitude,
    displayName,
    countryCode: String(value?.address?.country_code || "").trim().toUpperCase(),
    countryName: String(value?.address?.country || "").trim(),
  };
}

function cacheKey(query: string, countryCode: string) {
  return `${countryCode.trim().toUpperCase()}::${query.trim().toLowerCase()}`;
}

function cacheResults(key: string, results: CameraLocationSearchResult[]) {
  if (searchCache.size >= MAX_CACHE_ENTRIES) {
    const oldest = searchCache.keys().next().value;
    if (oldest) searchCache.delete(oldest);
  }
  searchCache.set(key, results);
}

async function respectPublicSearchRateLimit(signal?: AbortSignal) {
  const elapsed = Date.now() - lastRequestAt;
  const delay = Math.max(0, MIN_REQUEST_INTERVAL_MS - elapsed);
  if (!delay) return;

  await new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(resolve, delay);
    if (!signal) return;
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timer);
        reject(new DOMException("Search cancelled", "AbortError"));
      },
      { once: true },
    );
  });
}

export async function searchCameraLocations({
  query,
  countryCode = "",
  signal,
  locale = browserLocale(),
}: {
  query: string;
  countryCode?: string;
  signal?: AbortSignal;
  locale?: string;
}): Promise<CameraLocationSearchResult[]> {
  const normalizedQuery = query.trim();
  const normalizedCountryCode = countryCode.trim().toUpperCase();
  if (normalizedQuery.length < 2) return [];

  const key = cacheKey(normalizedQuery, normalizedCountryCode);
  const cached = searchCache.get(key);
  if (cached) return cached;

  await respectPublicSearchRateLimit(signal);
  if (signal?.aborted) throw new DOMException("Search cancelled", "AbortError");

  const params = new URLSearchParams({
    q: normalizedQuery,
    format: "jsonv2",
    addressdetails: "1",
    limit: "6",
    "accept-language": locale,
  });
  if (normalizedCountryCode) {
    params.set("countrycodes", normalizedCountryCode.toLowerCase());
  }

  lastRequestAt = Date.now();
  const response = await fetch(`${NOMINATIM_SEARCH_URL}?${params.toString()}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    signal,
  });
  if (!response.ok) {
    throw new Error("Location search is temporarily unavailable.");
  }

  const payload = await response.json();
  const results = (Array.isArray(payload) ? payload : [])
    .map(normalizeLocationSearchResult)
    .filter((item): item is CameraLocationSearchResult => Boolean(item));
  cacheResults(key, results);
  return results;
}
